import React, {useState, useMemo} from 'react';
import {useServiceProviderControl} from '../hooks/useServiceProviderControl';
import Button from '../../../Presentation/components/ui/Button';
import Card from '../../../Presentation/components/ui/Card';
import {AppText, AppSectionTitle, AppBodyText} from '../../../Presentation/components/ui/Typography';

interface Brand {
  id: number;
  name: string;
}

interface AssignBrandFormProps {
  providerId: number;
  availableBrands: Brand[];
  assignedBrands: Brand[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function AssignBrandForm ({
  providerId,
  availableBrands,
  assignedBrands,
  onSuccess,
  onCancel
}: AssignBrandFormProps) {
  const {assignBrand} = useServiceProviderControl();
  const [loading, setLoading] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<number | ''>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Filter out already assigned brands
  const unassignedBrands = useMemo(() => {
    if (!Array.isArray(availableBrands) || availableBrands.length === 0) {
      return [];
    }

    return availableBrands.filter(
      brand => !assignedBrands.some(assigned => assigned.id === brand.id)
    );
  }, [availableBrands, assignedBrands]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedBrandId) {
      newErrors.brand = 'يجب اختيار براند';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await assignBrand(providerId, selectedBrandId as number);
      onSuccess?.();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleBrandChange = (value: string) => {
    setSelectedBrandId(value ? parseInt(value) : '');

    // Clear error when user makes a selection
    if (errors.brand) {
      setErrors(prev => ({
        ...prev,
        brand: ''
      }));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card className="p-6">
        <div className="mb-6">
          <AppSectionTitle className="font-bold text-gray-900">
            ربط براند بمزود الخدمة
          </AppSectionTitle>
          <AppBodyText className="text-gray-600 mt-1">
            اختر البراند الذي تريد ربطه بمزود الخدمة
          </AppBodyText>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* البراندات المتاحة */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              اختر البراند *
            </label>
            <select
              value={selectedBrandId}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary/30 focus:border-brand-500"
            >
              <option value="">
                اختر براند...
              </option>
              {unassignedBrands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
            {errors.brand && (
              <p className="mt-1 text-sm text-red-600">{errors.brand}</p>
            )}
          </div>

          {/* البراندات المرتبطة حالياً */}
          {assignedBrands.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                البراندات المرتبطة حالياً:
              </h4>
              <div className="space-y-2">
                {assignedBrands.map((brand) => (
                  <div key={brand.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <span className="text-sm text-gray-900">
                      {brand.name}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      مرتبط
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* رسالة إذا لم تكن هناك براندات متاحة */}
          {unassignedBrands.length === 0 && (
            <div className="text-center py-6">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {availableBrands.length === 0
                  ? 'لا توجد براندات في النظام'
                  : 'جميع البراندات مرتبطة بالفعل'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {availableBrands.length === 0
                  ? 'لم يتم العثور على أي براندات في النظام. يرجى إضافة براندات أولاً من صفحة المنتجات.'
                  : `تم العثور على ${availableBrands.length} براند في النظام، وجميعها مرتبطة بهذا المزود بالفعل.`}
              </p>
              {availableBrands.length > 0 && assignedBrands.length > 0 && (
                <div className="mt-4 text-xs text-gray-400">
                  <p>البراندات المرتبطة: {assignedBrands.map(b => b.name).join(', ')}</p>
                </div>
              )}
            </div>
          )}

          {/* أزرار الإجراءات */}
          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              disabled={loading || unassignedBrands.length === 0}
              className="flex-1 bg-brand-600 hover:bg-brand-700 text-white px-6 py-2 rounded-md disabled:opacity-50"
            >
              {loading ? 'جاري الربط...' : 'ربط البراند'}
            </Button>
            {onCancel && (
              <Button
                type="button"
                onClick={onCancel}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md"
              >
                إلغاء
              </Button>
            )}
          </div>
        </form>

        {/* Guidelines */}
        <div className="mt-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
          <h4 className="text-sm font-semibold text-brand-900 mb-2">معلومات مهمة:</h4>
          <ul className="text-xs text-brand-800 space-y-1">
            <li>• يمكن ربط أكثر من براند بمزود الخدمة الواحد</li>
            <li>• البراندات المرتبطة حالياً لن تظهر في قائمة الاختيار</li>
            <li>• يمكن إلغاء الربط لاحقاً من صفحة تفاصيل مزود الخدمة</li>
          </ul>
        </div>
      </Card>
    </div>
  );
}
